import { useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { createDistributorSchema } from '@lombok/shared';
import type { CreateDistributorInput } from '@lombok/shared';
import { Button, Card, CardContent } from '@lombok/ui';
import { PageHeader } from '@/components/PageHeader';
import { FORM_COLUMN } from '@/components/layout';
import { BackLink } from '@/components/DetailRow';
import { FormActions, FormError, TextField } from '@/components/form';
import { TagsField } from '@/components/form/TagsField';
import { useCreateDistributor } from '@/data/hooks';

const BASE = '/app/admin/distribuidores';

/**
 * Create a distributor (admin). RHF + zodResolver against the shared
 * `createDistributorSchema` so client validation matches the backend exactly.
 * Territory and location ids are entered as chips; the backend remains the
 * authority and re-validates on POST.
 */
export function DistributorCreatePage() {
  const navigate = useNavigate();
  const mutation = useCreateDistributor();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<CreateDistributorInput>({
    resolver: zodResolver(createDistributorSchema),
    defaultValues: { name: '', rif: '', territoryIds: [], allowedLocationIds: [] },
  });

  const onSubmit = handleSubmit((values) => {
    mutation.mutate(values, {
      onSuccess: (created) => navigate(`${BASE}/${created.distributorId}`),
    });
  });

  return (
    <div className={`flex flex-col gap-6 ${FORM_COLUMN}`}>
      <div className="flex flex-col gap-2">
        <BackLink to={BASE}>Distribuidores</BackLink>
        <PageHeader title="Nuevo distribuidor" description="Registra una cuenta mayorista." />
      </div>

      <Card>
        <CardContent className="pt-5">
          <form onSubmit={onSubmit} className="flex flex-col gap-5" noValidate>
            <FormError error={mutation.error} />

            <TextField
              label="Nombre"
              required
              placeholder="Distribuidora Ejemplo, C.A."
              error={errors.name?.message}
              {...register('name')}
            />
            <TextField
              label="RIF"
              required
              placeholder="J-12345678-9"
              error={errors.rif?.message}
              {...register('rif')}
            />

            <Controller
              control={control}
              name="territoryIds"
              render={({ field, fieldState }) => (
                <TagsField
                  label="Territorios"
                  inputId="territoryIds"
                  value={field.value ?? []}
                  onChange={field.onChange}
                  hint="Identificadores de territorio asignados a este distribuidor."
                  error={fieldState.error?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="allowedLocationIds"
              render={({ field, fieldState }) => (
                <TagsField
                  label="Ubicaciones permitidas"
                  inputId="allowedLocationIds"
                  value={field.value ?? []}
                  onChange={field.onChange}
                  hint="Almacenes/ubicaciones desde donde puede abastecerse."
                  error={fieldState.error?.message}
                />
              )}
            />

            <FormActions>
              <Button type="button" variant="outline" onClick={() => navigate(BASE)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? 'Guardando…' : 'Crear distribuidor'}
              </Button>
            </FormActions>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
